/* eslint-disable prettier/prettier */
import {
  required,
  requiredIf,
  minLength,
  email,
  helpers
} from 'vuelidate/lib/validators'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import {
  isLessThanToday,
  isAtLeastAgo,
  isBiggerThan,
  nifPattern,
  isDateBetween,
  isDateLessThan,
  zipCode
} from '~/validations/validations.js'
import Form from '~/utils/mixins/form'

const phoneRule = helpers.withParams({ async: true }, (value, countryCode) => {
  return new Promise((resolve) => {
    const phoneNumber = countryCode + value // full phone number = countryCode+Value
    const parsedNumber = parsePhoneNumberFromString(phoneNumber) // now we can validate the full phone number

    resolve(parsedNumber ? parsedNumber.isValid() : false)
  })
})

const sameAs = (value) => value === true

export default {
  mixins: [Form],
  components: {
    InputBlock: () => import('~/components/commom/input-block/index'), /// Replace with lib component
    ErrorBlock: () => import('~/components/commom/error-block/index'), /// Replace with lib component
    DateBlock: () => import('~/components/commom/date-block/index'), /// Replace with lib component
    RadioBlock: () => import('~/components/commom/radio-block/index'),
    ButtonBlock: () => import('~/components/commom/button-block/index'),
    CheckboxBlock: () => import('~/components/commom/checkbox-block/index'),
    SelectBlock: () => import('~/components/commom/select-block/index')
  },
  data() {
    return {
      fullName: '',
      email: '',
      phone: '',
      countryCode: '+351',
      isCountryCode: true,
      insuredDob: null,
      licenseIssue: null,
      nif: '',
      yearsInsured: '',
      yearsNoAccidents: '',
      numberAccidents: '',
      postalCode: '',
      privacyPolicy: false,
      termsAndConditions: false,
      options: {
        countryCode: [
          {
            value: '+244',
            name: 'AO (+244)' // Angola
          },
          {
            value: '+43',
            name: 'AT (+43)' // Austria
          },
          {
            value: '+32',
            name: 'BE (+32)' // Belgium
          },
          {
            value: '+55',
            name: 'BR (+55)' // Brazil
          },
          {
            value: '+359',
            name: 'BG (+359)' // Bulgaria
          },
          {
            value: '+238',
            name: 'CV (+238)' // Cape Verde
          },
          {
            value: '+385',
            name: 'HR (+385)' // Croatia
          },
          {
            value: '+357',
            name: 'CY (+357)' // Cyprus
          },
          {
            value: '+420',
            name: 'CZ (+420)' // Czech Republic
          },
          {
            value: '+45',
            name: 'DK (+45)' // Denmark
          },
          {
            value: '+372',
            name: 'EE (+372)' // Estonia
          },
          {
            value: '+358',
            name: 'FI (+358)' // Finland
          },
          {
            value: '+33',
            name: 'FR (+33)' // France
          },
          {
            value: '+49',
            name: 'DE (+49)' // Germany
          },
          {
            value: '+30',
            name: 'GR (+30)' // Greece
          },
          {
            value: '+245',
            name: 'GW (+245)' // Guinea-Bissau
          },
          {
            value: '+36',
            name: 'HU (+36)' // Hungary
          },
          {
            value: '+353',
            name: 'IE (+353)' // Ireland
          },
          {
            value: '+39',
            name: 'IT (+39)' // Italy
          },
          {
            value: '+371',
            name: 'LV (+371)' // Latvia
          },
          {
            value: '+423',
            name: 'LI (+423)' // Liechtenstein
          },
          {
            value: '+370',
            name: 'LT (+370)' // Lithuania
          },
          {
            value: '+352',
            name: 'LU (+352)' // Luxembourg
          },
          {
            value: '+356',
            name: 'MT (+356)' // Malta
          },
          {
            value: '+258',
            name: 'MZ (+258)' // Mozambique
          },
          {
            value: '+31',
            name: 'NL (+31)' // Netherlands
          },
          {
            value: '+48',
            name: 'PL (+48)' // Poland
          },
          {
            value: '+351',
            name: 'PT (+351)' // Portugal
          },
          {
            value: '+40',
            name: 'RO (+40)' // Romania
          },
          {
            value: '+239',
            name: 'ST (+239)' // São Tomé and Príncipe
          },
          {
            value: '+421',
            name: 'SK (+421)' // Slovakia
          },
          {
            value: '+386',
            name: 'SI (+386)' // Slovenia
          },
          {
            value: '+34',
            name: 'ES (+34)' // Spain
          },
          {
            value: '+46',
            name: 'SE (+46)' // Sweden
          },
          {
            value: '+41',
            name: 'CH (+41)' // Switzerland
          },
          {
            value: '+44',
            name: 'UK (+44)' // United Kingdom
          },
          {
            value: '+1',
            name: 'USA (+1)' // United States
          }
        ]
      }
    }
  },

  computed: {
    firstName() {
      // Split the fullName into first name and last name
      return this.fullName.split(' ')[0] || ''
    },
    lastName() {
      // Split the fullName into first name and last name
      return (
        this.fullName
          .split(' ')
          .slice(1)
          .join(' ') || ''
      )
    },
    showNumberOfAccidents() {
      if (this.yearsNoAccidents === null || this.yearsInsured === null) {
        return false
      }

      return this.yearsInsured > this.yearsNoAccidents
    },
    personalDetails() {
      return {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.countryCode + this.phone,
        insuredDob: this.insuredDob,
        licenseIssue: this.licenseIssue,
        nif: this.nif,
        yearsInsured: this.yearsInsured,
        yearsNoAccidents: this.yearsNoAccidents,
        numberAccidents: this.numberAccidents,
        postalCode: this.postalCode
      }
    }
  },

  validations() {
    const baseValidation = {
      fullName: {
        required,
        minLength: minLength(2),
        custom(value) {
          const names = value.split(' ')
          return names.length >= 2 && names.every(name => name.length >= 2)
        }
      },
      email: {
        required,
        email
        // emailDomain,
      },
      phone: {
        required,
        phoneRule: (value) => phoneRule(value, this.countryCode)
      },
      insuredDob: {
        required,
        isLessThanToday,
        isAtLeastAgo: isAtLeastAgo(new Date(), 18),
        isDateLessThan: isDateLessThan(100)
      },
      licenseIssue: {
        required,
        isLessThanToday,
        isBiggerThan: isBiggerThan('insuredDob', 18)
      },
      nif: {
        required,
        nifPattern
      },
      yearsInsured: {
        required,
        isDateBetween: isDateBetween('licenseIssue', new Date())
      },
      yearsNoAccidents: {
        required,
        maxValue(value) {
          return this.yearsInsured >= value
        }
      },
      numberAccidents: {
        required: requiredIf(function() {
          return this.showNumberOfAccidents
        })
      },
      postalCode: {
        required,
        zipCode
      },
      privacyPolicy: {
        required,
        sameAs
      },
      termsAndConditions: {
        required,
        sameAs
      },
      countryCode: {
        required
      }
    }
    return baseValidation
  },

  watch: {
    yearsNoAccidents() {
      if (!this.showNumberOfAccidents) {
        this.numberAccidents = 0
      }
    }
  },
  mounted() {
    // eslint-disable-next-line no-console
  },
  methods: {
    focusNextInput(ref) {
      this.$nextTick(() => {
        this.$refs[ref].$el.querySelector('input').focus()
      })
    },
    validateForm() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        this.$nextTick(() => this.goToFirstError())
      }
    },
    goToNextStep() {
      this.validateForm()

      if (this.$v.$invalid) {
        return
      }
      this.$emit('input', this.personalDetails)
    },
    scrollTo(element) {
      const elementPosition =
        element.getBoundingClientRect().top + document.documentElement.scrollTop
      const halfOfViewport =
        Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        ) / 2

      window.scrollTo(0, elementPosition - halfOfViewport)
    }
  }
}
